/**
 * Abrir a trava com a chave que já está **no aparelho** — sem rede.
 *
 * Por que isto não é `entrarComPasskey`: aquele é o **login**, e vai ao
 * GoTrue (`signInWithPasskey`). Usá-lo como portão local significava que
 * destrancar o app exigia sinal de celular, e a trava fecha exatamente onde
 * ele falta: sair do app para abrir o Mapas, voltar seis minutos depois no
 * meio do campo, sem barra — e o banco inteiro atrás de uma porta que
 * depende de servidor. Aqui é `navigator.credentials.get` puro: o WebAuthn
 * do aparelho pede Face ID, devolve a asserção, e pronto.
 *
 * **A asserção é aceita sem verificação de assinatura, de propósito.**
 * Verificar exigiria a chave pública guardada em algum lugar e, na prática,
 * um servidor — ou seja, rede outra vez. E não acrescentaria nada contra a
 * ameaça que esta trava declara: um telefone emprestado, **já
 * desbloqueado**, na mão de quem abre o app por curiosidade. Quem
 * conseguisse forjar uma asserção nesta origem já teria o aparelho
 * desbloqueado e um depurador, e nesse caso lê o IndexedDB em claro sem
 * passar por tela nenhuma (ver `docs/pendencias.md`: portão, não cofre).
 *
 * **O que de fato protege, e até onde.** O `userVerification: 'required'`
 * garante que alguém teve de confirmar identidade — mas **em qual
 * aparelho** é outra pergunta, e a resposta honesta é: em qualquer um.
 * Sem `allowCredentials`, este `get` aceita **qualquer** passkey desta
 * origem, inclusive uma que viva noutro aparelho e chegue pelo transporte
 * híbrido (o fluxo do QR code do iOS). Nesse caminho o Face ID que
 * satisfaz o `userVerification` é o **do invasor, no aparelho dele** — e o
 * app abre. Fixar `allowCredentials` fecharia isso, e não foi feito aqui
 * por um motivo concreto: o id da credencial não é guardado localmente, e
 * inventar esse armazenamento (quando gravar, o que fazer com o aparelho
 * que já tem a trava ligada e nenhum id guardado, como apagar) é mudança
 * maior do que a desta rodada. Registrado em `docs/pendencias.md` com o
 * conserto escrito.
 *
 * A sessão do Supabase não é tocada: destrancar não renova, não invalida e
 * não emite token nenhum.
 */

export class ErroDaTrava extends Error {
  override readonly name = 'ErroDaTrava';
}

/** O tanto de aparelho que a trava precisa enxergar. Escrito como forma, e
 *  não como `typeof globalThis`, para o teste injetar um dublê. */
export interface AparelhoComChave {
  PublicKeyCredential?: unknown;
  navigator?: {
    credentials?: {
      get?: (opcoes: CredentialRequestOptions) => Promise<Credential | null>;
    };
  };
  crypto?: { getRandomValues?: <T extends Uint8Array>(alvo: T) => T };
}

/**
 * Dá para desbloquear neste aparelho e nesta origem?
 *
 * É o mesmo portão que a opção usa em Ajustes › Conta, e por isso vale
 * também para **não ligar** a trava onde ela não abriria: preferência ligada
 * mais WebAuthn ausente (outra origem, navegador sem suporte) era o app
 * trancado com "Desbloquear" falhando para sempre e nenhum caminho até
 * Ajustes para desligar a opção.
 */
export function travaDisponivel(
  escopo: AparelhoComChave = globalThis,
): boolean {
  return (
    escopo.PublicKeyCredential !== undefined &&
    typeof escopo.navigator?.credentials?.get === 'function'
  );
}

/** 32 bytes de desafio. Ninguém do outro lado o confere — ele existe para o
 *  aparelho não reaproveitar asserção antiga e porque a API o exige. */
const BYTES_DO_DESAFIO = 32;

/**
 * Pede a chave do aparelho. Resolver é "abre"; rejeitar é "continua
 * trancado, tenta de novo" — nunca "desloga".
 */
export async function abrirComChaveDoAparelho(
  escopo: AparelhoComChave = globalThis,
): Promise<void> {
  const pegar = escopo.navigator?.credentials?.get;
  const sortear = escopo.crypto?.getRandomValues;
  if (typeof pegar !== 'function' || typeof sortear !== 'function')
    throw new ErroDaTrava(
      'Este aparelho não tem como confirmar sua identidade.',
    );
  let credencial: Credential | null;
  try {
    const desafio = new Uint8Array(BYTES_DO_DESAFIO);
    // A API exige a instância de Crypto como `this`, inclusive no Safari.
    sortear.call(escopo.crypto, desafio);
    credencial = await pegar.call(escopo.navigator?.credentials, {
      publicKey: {
        challenge: desafio,
        // Sem isto o aparelho pode devolver uma asserção sem pedir nada ao
        // usuário, e a trava viraria um botão "Entrar". (Sem
        // `allowCredentials`, porém, ele não diz **em qual** aparelho a
        // confirmação aconteceu — ver o cabeçalho.)
        userVerification: 'required',
        timeout: 60_000,
      },
    });
  } catch (e) {
    throw traduzirErroDaChave(e);
  }
  // Só uma asserção de verdade abre. `credencial === null` não bastava: o
  // tipo diz `Credential | null`, mas o valor que chega vem do aparelho, e
  // um `undefined` — de uma implementação que resolve sem devolver nada, ou
  // de um dublê malfeito — passava pela comparação estrita e **abria o
  // app**. `== null` cobre os dois, e a conferência de forma cobre o resto:
  // o que abre a trava tem de parecer uma `PublicKeyCredential`.
  if (!ehAssercao(credencial))
    throw new ErroDaTrava('Face ID não confirmou. Toque para tentar de novo.');
}

/**
 * Parece a asserção que o WebAuthn devolve?
 *
 * Deliberadamente frouxa: um `id` que não é vazio e uma `response`. Não se
 * confere assinatura aqui (o cabeçalho diz por quê), e apertar mais — exigir
 * campos que só o Safari do aparelho produz — arriscaria recusar a chave
 * real num caminho que nenhum teste desta suíte percorre.
 */
function ehAssercao(c: unknown): boolean {
  if (c === null || typeof c !== 'object') return false;
  const { id, response } = c as { id?: unknown; response?: unknown };
  return (
    typeof id === 'string' &&
    id !== '' &&
    typeof response === 'object' &&
    response !== null
  );
}

/** Nomes de erro do WebAuthn em português de gente. O texto vai para um
 *  `role="alert"` na tela trancada, então ele diz o que fazer, não o que
 *  aconteceu por dentro. */
export function traduzirErroDaChave(e: unknown): Error {
  const nome = e instanceof Error ? e.name : '';
  if (nome === 'NotAllowedError')
    return new ErroDaTrava('Face ID não confirmou. Toque para tentar de novo.');
  if (nome === 'InvalidStateError' || nome === 'NotSupportedError')
    return new ErroDaTrava(
      'Não há chave neste aparelho para abrir o app. Use "Não consigo desbloquear".',
    );
  if (nome === 'SecurityError')
    return new ErroDaTrava(
      'O endereço deste app mudou e a chave não vale aqui. Use "Não consigo desbloquear".',
    );
  return new ErroDaTrava(
    e instanceof Error && e.message !== '' ? e.message : 'Não deu para abrir.',
  );
}
